import { CheckCircleRounded, CircleOutlined, Close, DoNotDisturbOnRounded, ExpandMore, Replay, Save } from '@mui/icons-material';
import { Checkbox, CircularProgress, Divider, InputBase, InputBaseProps, List, ListItem, ListItemButton, Stack, Typography, debounce } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ResponseArray, ResponseObject } from '../common/common.entity';
import { APIParams } from '../pages/userMangement/role/apiCall';
import { setReducerState } from '../redux/crudSlice';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { MuiIconButton } from './MuiIconButton';
import { MuiStack } from './MuiStack';

const initSelection = { count: 0 }

function CheckListSearch(props: any) {
    const crud = useAppSelector(state => state.crud);
    const {
        apiKey = 'multiselectList',
        displayExpr = 'Name',
        valueExpr = 'id',
        getOptions,
        label = 'Title',
        onChange,
        getOptionsLabel = (option: ResponseObject) => option[displayExpr],
        name = 'Name',
        onSubmit = (e?: any) => { },
        onClose = (e?: any) => { },
        pagingAPIKey = '',
        filterMode = 'client'
    } = props
    const [options, setOptions] = useState<ResponseArray>([]);
    const [selected, setSelected] = useState<ResponseObject>(initSelection);
    const [search, setSearch] = useState<string>('');
    const dispatch = useAppDispatch();
    let loading: boolean = crud['isLoading' + apiKey] === 'loading';
    const listPagingKey = apiKey + 'Paging'

    useEffect(() => {
        getData({ key: apiKey });
    }, [])

    const checkSelected = useCallback((selection: typeof selected, data: typeof options) => {
        if (!selection?.count || !data?.length) return data;

        const newOptions = data.map(item => {
            const flag = Boolean(selection[item[valueExpr]]);
            if (flag) return {
                ...item,
                selected: selection[item[valueExpr]]
            }
            else return item
        });
        return newOptions;
    }, [])

    const getData = async (params: APIParams) => {
        let res: any;
        if (getOptions) {
            res = await getOptions(params);
            res = checkSelected(selected, res?.data);
            if (params.key === listPagingKey) res = [...options, ...res];
            setOptions(res || []);
        }
    }

    const handleClick = useCallback(async (e: any) => {
        const id = (e.target.id);
        if (!id) return;
        if (id === 'show-more') {
            await getData({ key: listPagingKey, request: { skip: options.length, take: 25 } });
            return;
        }

        let newValues = { ...selected };

        if (id === 'toggle-all') {
            const isSelected = !(selected.count === options.length)
            if (isSelected) options.forEach(item => {
                if (!newValues[item[valueExpr]]) {
                    newValues[item[valueExpr]] = item
                    newValues.count += 1;
                }
            });
            else newValues = initSelection
        } else if (id === 'clear-all') {
            newValues = initSelection
        } else {
            let isSelected = !selected[id];
            if (isSelected) newValues[id] = options.find((item) => item[valueExpr] === id);
            else delete newValues[id];

            newValues.count += isSelected ? 1 : -1;
        }
        setSelected(newValues);
        if (onChange) onChange(e, Object.values(newValues));
    }, [options, selected])

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        let selection = { ...selected }
        delete selection.count;

        const request = {
            [name]: Object.keys(selection)
        }

        const res = await onSubmit({ request: request });
        if (res?.success) {
            onClose(e);
            dispatch(setReducerState({ key: (pagingAPIKey + 'Updated'), value: true }));
            setSelected(initSelection);
        }
    }

    const searchOptions = useMemo(() => {
        let res = options?.filter((item) => {
            const name: string = item[displayExpr]?.toLowerCase();
            const input: string = search?.toLowerCase();
            return name?.includes(input);
        })
        return res;
    }, [search, options])

    const onInputChange: InputBaseProps['onChange'] = useCallback(debounce(async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const input = e.target.value;
        if (filterMode === 'server') {
            const request = { search: input };
            await getData({ key: apiKey, request: request });
        } else {
            setSearch(input)
        }
    }, 500), [filterMode]);

    return (
        <Stack maxHeight='100%'>
            {/* Page Title */}
            <MuiStack justifyContent='start' alignItems='center' >
                <Typography variant='h2' component='span' width='100%' px={1} >{label}</Typography>
                <Typography variant='subtitle1' color='gray' px={0.5} mx={1} bgcolor='rgb(240,240,240)' borderRadius={1} > {selected.count}/{options.length} </Typography>

                <MuiIconButton size='small' onClick={handleSubmit} > <Save fontSize='small' /> </MuiIconButton>
                <MuiIconButton size='small' onClick={onClose} > <Close fontSize='small' /> </MuiIconButton>
            </MuiStack>
            <Divider flexItem />

            {/* List Container */}
            <Stack
                direction='column'
                overflow='hidden'
                position='relative'
                borderRadius={2}
                flexGrow={1}
                m={1}
                sx={{ outline: '1px solid rgb(230,230,230)' }}
                onClick={handleClick}
            >
                {/* List Header */}
                <ListItem
                    sx={{
                        p: '0px 10px',
                        color: 'rgb(50,50,50)',
                        bgcolor: 'rgb(240,240,240)',
                        borderBottom: '1px solid rgb(230,230,230)',
                        position: 'sticky',
                        top: 0,
                        zIndex: 2
                    }}
                >
                    <Checkbox
                        id={'toggle-all'}
                        checked={selected.count === options.length}
                        indeterminate={selected.count && selected.count < options.length}
                        disableRipple
                        sx={{
                            pr: 2,
                            color: 'rgb(150,150,150)',
                            borderRadius: '50%',
                            '&.Mui-checked': {
                                color: 'rgb(50,50,50)'
                            }
                        }}
                        icon={<CircleOutlined />}
                        checkedIcon={<CheckCircleRounded />}
                        indeterminateIcon={<DoNotDisturbOnRounded />}
                    />
                    <InputBase
                        name='search'
                        placeholder='Search'
                        onChange={onInputChange}
                        fullWidth
                    />
                    <MuiIconButton id='clear-all' tooltipTitle='Clear Selection' size='small' color='secondary' disableRipple >
                        <Replay id='clear-all' fontSize='small' />
                    </MuiIconButton>
                </ListItem>

                {/* List Body */}
                <List sx={{ overflowY: 'auto', p: 1, flexGrow: 1 }} >
                    {loading ? //loader
                        <MuiStack>
                            <CircularProgress size={30} sx={{ color: 'rgb(150,150,150)' }} />
                        </MuiStack>
                        : //list of options
                        (filterMode === 'server' ? options : searchOptions).map((item: ResponseObject) => (
                            <ListItemButton
                                id={item[valueExpr]}
                                key={item[valueExpr]}
                                sx={{
                                    mb: '2px',
                                    p: '2px 2px',
                                    color: selected[item[valueExpr]] ? 'rgb(50,50,50)' : 'rgb(100,100,100)',
                                    borderRadius: '4px',
                                    lineHeight: '1.8ch',
                                    letterSpacing: 0.2
                                }}
                                selected={Boolean(selected[item[valueExpr]])}
                                disableRipple
                            >
                                <Checkbox
                                    id={item[valueExpr]}
                                    checked={Boolean(selected[item[valueExpr]])}
                                    disableRipple
                                    sx={{
                                        pr: 2,
                                        color: 'rgb(150,150,150)',
                                        borderRadius: '50%',
                                        '&.Mui-checked': {
                                            color: 'rgb(50,50,50)'
                                        }
                                    }}
                                    icon={<CircleOutlined />}
                                    checkedIcon={<CheckCircleRounded />}
                                />
                                {getOptionsLabel(item)}
                            </ListItemButton>
                        ))
                    }
                    {crud[apiKey]?.count > options.length && //list footer button
                        <ListItemButton
                            id={'show-more'}
                            sx={{
                                mb: '2px',
                                p: 0,
                                color: 'rgb(100,100,100)',
                                borderRadius: '4px',
                                display: 'flex',
                                justifyContent: 'center',
                            }}
                        >
                            {crud['isLoading' + listPagingKey] === 'loading'
                                ? 'Loading Options...'
                                : <ExpandMore id={'show-more'} />
                            }
                        </ListItemButton>}
                </List>
            </Stack>
        </Stack>
    )
}


export default CheckListSearch
